import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiSave, FiMessageSquare, FiPlus, FiEdit2, FiTrash2, FiMoon,
  FiUsers, FiUser, FiRadio, FiBell, FiGrid, FiCpu, FiCheck, FiStar, FiZap, FiBarChart2, FiCalendar,
} from 'react-icons/fi';
import { getSectors, Sector } from '../services/SectorService';
import SessionService from '../services/SessionService';
import Toast, { ToastContainer } from '../components/Toast';
import './AIAgentPage.css';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ConfirmModal, SharedModal } from '../components/SharedModal';
import {
  listUnifiedAgents,
  createUnifiedAgent,
  updateUnifiedAgent,
  deleteUnifiedAgent,
  getSectorAiConfig,
  saveSectorAiConfig,
  type UnifiedAiAgentDTO,
  type AiAgentCapability,
  CAPABILITY_SECTION_META,
} from '../services/UnifiedAIAgentService';
import { optimizeText } from '../services/TextOptimizationService';
import { timezoneDisplayLabel } from '../utils/dateTimeUtils';
import { apiErrorMessage } from '../utils/apiErrorMessage';
import { config } from '../config';
import { useModules } from '../contexts/ModuleContext';
import DocumentsTab from '../components/DocumentsTab';
import AiSandboxPanel from '../components/AiSandboxPanel';
import AgentEvalPanel from '../components/AgentEvalPanel';
import { getQuoteDocument, listAssignedDocumentTemplates, type AiDocumentTemplateOption, type SectorAiDocumentConfig } from '../services/SectorDocumentService';
import {
  buildDocumentPromptContext,
  isInboundDocumentAgent,
  isPdfQuoteModuleActive,
  mergeTemplateOptions,
  suggestAgentKnowledgeFromDocumentTemplate,
  suggestAgentPromptFromDocumentTemplate,
} from '../utils/aiDocumentUtils';
import { agentCapabilityLabels, agentStatusLabel } from '../mendsAi/lib/agentDisplay';
import { fetchCentralOverview, isMendsVisionEnabled, type CentralOverview } from '../mendsAi/services/MendsVisionService';
import { CentralAgentCards } from '../mendsAi/components/central/CentralAgents';
import { AgentPerformance } from '../mendsAi/components/AgentPerformance';
import '../mendsAi/styles/central.css';

interface ToastItem {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

type AgentPreset = 'receptive' | 'campaign' | 'followup' | 'crm';

const PRESET_CAPS: Record<AgentPreset, { r: boolean; c: boolean; f: boolean; cr: boolean }> = {
  receptive: { r: true, c: false, f: false, cr: false },
  campaign: { r: false, c: true, f: false, cr: false },
  followup: { r: false, c: false, f: true, cr: false },
  crm: { r: false, c: false, f: false, cr: true },
};

const PROMPT_PLACEHOLDERS: Record<AgentPreset, string> = {
  receptive:
    'Você atende os clientes de [sua empresa]. Responda no idioma do cliente, com objetividade, cordialidade e precisão.',
  campaign:
    'Cuide das respostas depois dos disparos de campanha. Confirme o interesse, tire dúvidas sobre o produto e conduza ao próximo passo, no idioma do cliente.',
  followup:
    'Avalie as regras de follow-up e escreva mensagens para retomar a conversa, no tom e no idioma do cliente.',
  crm:
    'Apoie o funil: entenda o contexto de cada conversa para que as condições das colunas sejam avaliadas com precisão.',
};

const KNOWLEDGE_PLACEHOLDER =
  'Produtos, preços, perguntas frequentes, políticas, horário de atendimento e tudo o que a IA precisa saber sobre a sua empresa.';

const CATEGORY_ORDER: AiAgentCapability[] = ['receptive', 'campaign', 'followup', 'crm'];
const CREATE_PRESETS: AgentPreset[] = ['receptive', 'campaign', 'followup', 'crm'];

const CATEGORY_ICONS: Record<AiAgentCapability, React.ReactNode> = {
  receptive: <FiMessageSquare size={18} />,
  campaign: <FiRadio size={18} />,
  followup: <FiBell size={18} />,
  crm: <FiGrid size={18} />,
};

function agentsForCapability(agents: UnifiedAiAgentDTO[], cap: AiAgentCapability): UnifiedAiAgentDTO[] {
  switch (cap) {
    case 'receptive': return agents.filter(a => a.capReceptive);
    case 'campaign': return agents.filter(a => a.capCampaign);
    case 'followup': return agents.filter(a => a.capFollowup);
    case 'crm': return agents.filter(a => a.capCrm);
    default: return [];
  }
}

function pickDefaultReceptiveAgent(
  agents: UnifiedAiAgentDTO[],
  currentId: number | null | undefined
): number | null {
  const receptive = agents.filter(a => a.capReceptive && a.isEnabled);
  if (currentId && receptive.some(a => a.id === currentId)) return currentId;
  return receptive[0]?.id ?? null;
}

type AgentRowProps = {
  agent: UnifiedAiAgentDTO;
  isDefault?: boolean;
  onEdit: (a: UnifiedAiAgentDTO) => void;
  onDelete: (a: UnifiedAiAgentDTO) => void;
  onSetDefault?: (a: UnifiedAiAgentDTO) => void;
};

const AgentRow: React.FC<AgentRowProps> = ({ agent, isDefault, onEdit, onDelete, onSetDefault }) => (
  <li className="ai-hub-agent-card">
    <div className="ai-hub-agent-card__main">
      <span
        className={`ai-hub-agent-card__status ${agent.isEnabled ? 'ai-hub-agent-card__status--on' : 'ai-hub-agent-card__status--off'}`}
        title={agentStatusLabel(agent.isEnabled)}
      />
      <div className="ai-hub-agent-card__text">
        <div className="ai-hub-agent-card__name-row">
          <span className="ai-hub-agent-card__name">{agent.name}</span>
          {isDefault && <span className="ai-hub-badge-default">Padrão</span>}
          {!agent.isEnabled && <span className="ai-hub-badge-off">Pausado</span>}
        </div>
      </div>
    </div>
    <div className="ai-hub-agent-card__actions">
      {onSetDefault && !isDefault && agent.isEnabled && (
        <button
          type="button"
          className="ai-hub-agent-card__action ai-hub-agent-card__action--default"
          onClick={() => onSetDefault(agent)}
          title="Usar como funcionário padrão do atendimento"
          aria-label="Usar como padrão"
        >
          <FiStar size={14} />
          <span>Padrão</span>
        </button>
      )}
      <button type="button" className="ai-hub-agent-card__action" onClick={() => onEdit(agent)} title="Editar" aria-label="Editar">
        <FiEdit2 size={15} />
      </button>
      <button type="button" className="ai-hub-agent-card__action ai-hub-agent-card__action--danger" onClick={() => onDelete(agent)} title="Excluir" aria-label="Excluir">
        <FiTrash2 size={15} />
      </button>
    </div>
  </li>
);

/** Quem está trabalhando no setor: o resumo que antes ficava na barra lateral. */
const DigitalTeamRoster: React.FC<{ agents: UnifiedAiAgentDTO[]; onEdit: (a: UnifiedAiAgentDTO) => void }> = ({ agents, onEdit }) => (
  <section className="ai-hub-roster" aria-label="Equipe digital">
    <div className="ai-hub-roster__head">
      <h3>Equipe digital</h3>
      <span>Quem está trabalhando neste setor agora</span>
    </div>
    {agents.length === 0 ? (
      <p className="ai-hub-roster__empty">Nenhum funcionário neste setor ainda.</p>
    ) : (
      <ul className="ai-hub-roster__list">
        {agents.map(a => {
          const roles = agentCapabilityLabels(a);
          return (
            <li key={a.id}>
              <button type="button" className="ai-hub-roster__item" onClick={() => onEdit(a)} title={`Editar ${a.name}`}>
                <span className={`ai-hub-roster__dot ${a.isEnabled ? 'is-on' : 'is-off'}`} aria-hidden />
                <span className="ai-hub-roster__meta">
                  <strong>{a.name}</strong>
                  <small>
                    <span className={a.isEnabled ? 'is-on' : 'is-off'}>{agentStatusLabel(a.isEnabled)}</span>
                    {roles.length > 0 ? ` · ${roles.join(' · ')}` : ''}
                  </small>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    )}
  </section>
);

type CategorySectionProps = {
  capability: AiAgentCapability;
  agents: UnifiedAiAgentDTO[];
  defaultAgentId?: number | null;
  onCreate: (preset: AgentPreset) => void;
  onEdit: (a: UnifiedAiAgentDTO) => void;
  onDelete: (a: UnifiedAiAgentDTO) => void;
  onSetDefault?: (a: UnifiedAiAgentDTO) => void;
  children?: React.ReactNode;
};

const CategorySection: React.FC<CategorySectionProps> = ({
  capability,
  agents,
  defaultAgentId,
  onCreate,
  onEdit,
  onDelete,
  onSetDefault,
  children,
}) => {
  const meta = CAPABILITY_SECTION_META[capability];
  const preset = capability === 'receptive' ? 'receptive' : capability;

  return (
    <section className={`ai-hub-category ai-hub-category--${capability}`}>
      <div className="ai-hub-category__header">
        <div className={`ai-hub-category__icon ai-hub-category__icon--${capability}`}>
          {CATEGORY_ICONS[capability]}
        </div>
        <div className="ai-hub-category__titles">
          <h3 className="ai-hub-category__title">{meta.title}</h3>
          <p className="ai-hub-category__used-in">{meta.usedIn}</p>
        </div>
        <button
          type="button"
          className="ai-hub-category__add"
          onClick={() => onCreate(preset as AgentPreset)}
        >
          <FiPlus size={15} /> Novo
        </button>
      </div>
      <p className="ai-hub-category__description">{meta.description}</p>

      <div className="ai-hub-category__agents">
        <div className="ai-hub-category__agents-label">
          <span>Funcionários</span>
          <span className="ai-hub-category__count">{agents.length}</span>
        </div>
        {agents.length === 0 ? (
          <div className="ai-hub-category__empty">
            <p>Nenhum funcionário neste grupo ainda.</p>
            <button type="button" className="ai-hub-category__empty-btn" onClick={() => onCreate(preset as AgentPreset)}>
              <FiPlus size={14} /> Criar funcionário
            </button>
          </div>
        ) : (
          <ul className="ai-hub-agent-list">
            {agents.map(a => (
              <AgentRow
                key={a.id}
                agent={a}
                isDefault={capability === 'receptive' && defaultAgentId === a.id}
                onEdit={onEdit}
                onDelete={onDelete}
                onSetDefault={onSetDefault}
              />
            ))}
          </ul>
        )}
      </div>

      {children}
    </section>
  );
};

const AIAgentPage: React.FC = () => {
  const [currentSector, setCurrentSector] = useState<Sector | null>(null);
  const [agents, setAgents] = useState<UnifiedAiAgentDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const [receptiveEnabled, setReceptiveEnabled] = useState(false);
  const [defaultReceptiveAgentId, setDefaultReceptiveAgentId] = useState<number | null>(null);
  const [defaultReceptiveAgentName, setDefaultReceptiveAgentName] = useState('');
  const [midnightReactivation, setMidnightReactivation] = useState(false);
  const [profileTimezone, setProfileTimezone] = useState('UTC');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UnifiedAiAgentDTO | null>(null);
  const [formPreset, setFormPreset] = useState<AgentPreset>('receptive');
  const [formName, setFormName] = useState('');
  const [formEnabled, setFormEnabled] = useState(true);
  const [formPrompt, setFormPrompt] = useState('');
  const [formKnowledge, setFormKnowledge] = useState('');
  const [formAutoTransfer, setFormAutoTransfer] = useState(true);
  const [formCalendarEnabled, setFormCalendarEnabled] = useState(true);
  const [formHumanTransferEnabled, setFormHumanTransferEnabled] = useState(false);
  const [formHumanTransferRules, setFormHumanTransferRules] = useState('');
  const [hubTab, setHubTab] = useState<'agents' | 'documents'>('agents');
  const [showSandbox, setShowSandbox] = useState(false);
  const [showEval, setShowEval] = useState(false);
  const { hasModule } = useModules();
  const showDocumentsTab = hasModule('aiDocuments');
  const [searchParams, setSearchParams] = useSearchParams();
  const [aiOptimizing, setAiOptimizing] = useState<'prompt' | 'knowledge_base' | null>(null);
  const [quoteDoc, setQuoteDoc] = useState<SectorAiDocumentConfig | null>(null);
  const [quoteTemplates, setQuoteTemplates] = useState<AiDocumentTemplateOption[]>([]);

  const quotePromptCtx = useMemo(
    () => buildDocumentPromptContext(quoteDoc, quoteTemplates),
    [quoteDoc, quoteTemplates]
  );
  const pdfModuleActive = isPdfQuoteModuleActive(quoteDoc);
  const showPdfAgentSuggest = pdfModuleActive && formPreset === 'receptive';

  const location = useLocation();
  const navigate = useNavigate();
  const navFocusHandled = useRef<string | null>(null);
  /** Saúde e carga por funcionário (mesmo cálculo da Central de Inteligência, últimos 7 dias). */
  const [teamHealth, setTeamHealth] = useState<CentralOverview | null>(null);
  const [teamHealthState, setTeamHealthState] = useState<'idle' | 'loading' | 'error'>('idle');

  const agentsByCategory = useMemo(() => {
    const map = {} as Record<AiAgentCapability, UnifiedAiAgentDTO[]>;
    for (const cap of CATEGORY_ORDER) {
      map[cap] = agentsForCapability(agents, cap);
    }
    return map;
  }, [agents]);

  const hubStats = useMemo(() => ({
    total: agents.length,
    active: agents.filter(a => a.isEnabled).length,
    receptive: agentsByCategory.receptive.length,
    outbound: agentsByCategory.campaign.length + agentsByCategory.followup.length + agentsByCategory.crm.length,
  }), [agents, agentsByCategory]);

  const inboundAiAvailable = useMemo(
    () => receptiveEnabled || agents.some(isInboundDocumentAgent),
    [receptiveEnabled, agents]
  );

  const addToast = (message: string, type: 'success' | 'error' | 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const loadCurrentSector = useCallback(async () => {
    try {
      const token = SessionService.getToken();
      const sectorId = SessionService.getSectorId();
      if (!token || !sectorId) {
        setCurrentSector(null);
        return;
      }
      const sectorsList = await getSectors(token);
      setCurrentSector(sectorsList.find(s => s.id === sectorId) ?? null);
    } catch {
      setCurrentSector(null);
    }
  }, []);

  const loadAll = useCallback(async (sectorId: number) => {
    const token = SessionService.getToken();
    if (!token) return;
    setIsLoading(true);
    try {
      const [agentList, cfg] = await Promise.all([
        listUnifiedAgents(sectorId, token),
        getSectorAiConfig(sectorId, token),
      ]);
      setAgents(agentList);
      setReceptiveEnabled(cfg.receptiveAiEnabled);
      const resolvedDefaultId = pickDefaultReceptiveAgent(agentList, cfg.defaultReceptiveAgentId);
      setDefaultReceptiveAgentId(resolvedDefaultId);
      setMidnightReactivation(cfg.midnightAiReactivationEnabled);
      if (resolvedDefaultId) {
        const def = agentList.find(a => a.id === resolvedDefaultId);
        setDefaultReceptiveAgentName(def?.name ?? '');
      } else {
        setDefaultReceptiveAgentName('');
      }
    } catch (e) {
      console.error('Error loading AI hub:', e);
      addToast('Falha ao carregar funcionários', 'error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchParams.get('tab') === 'documents' && showDocumentsTab) {
      setHubTab('documents');
    }
  }, [searchParams, showDocumentsTab]);

  useEffect(() => {
    if (!currentSector?.id || !showDocumentsTab) {
      setQuoteDoc(null);
      setQuoteTemplates([]);
      return;
    }
    void (async () => {
      try {
        const [doc, assigned] = await Promise.all([
          getQuoteDocument(currentSector.id),
          listAssignedDocumentTemplates(),
        ]);
        setQuoteDoc(doc);
        setQuoteTemplates(mergeTemplateOptions(assigned, doc));
      } catch {
        setQuoteDoc(null);
        setQuoteTemplates([]);
      }
    })();
  }, [currentSector?.id, showDocumentsTab]);

  const openDocumentsTab = () => {
    setHubTab('documents');
    setSearchParams({ tab: 'documents' }, { replace: true });
  };

  const openAgentsTab = () => {
    setHubTab('agents');
    setSearchParams({}, { replace: true });
  };

  useEffect(() => {
    loadCurrentSector();
    const onSectorChange = () => loadCurrentSector();
    window.addEventListener('sectorChanged', onSectorChange);
    return () => window.removeEventListener('sectorChanged', onSectorChange);
  }, [loadCurrentSector]);

  useEffect(() => {
    if (currentSector?.id) loadAll(currentSector.id);
  }, [currentSector?.id, loadAll]);

  useEffect(() => {
    const sid = currentSector?.id;
    setTeamHealth(null);
    if (!sid || !isMendsVisionEnabled()) return;
    let cancelled = false;
    setTeamHealthState('loading');
    fetchCentralOverview([sid], '7d')
      .then((o) => { if (!cancelled) { setTeamHealth(o); setTeamHealthState('idle'); } })
      .catch(() => { if (!cancelled) setTeamHealthState('error'); });
    return () => { cancelled = true; };
  }, [currentSector?.id]);

  useEffect(() => {
    const focus = (location.state as { focusCapability?: AiAgentCapability } | null)?.focusCapability;
    if (!focus || !currentSector?.id) return;
    const token = `${location.key}-${focus}`;
    if (navFocusHandled.current === token) return;
    navFocusHandled.current = token;
    const preset: AgentPreset =
      focus === 'receptive' ? 'receptive' : focus === 'campaign' ? 'campaign' : focus === 'followup' ? 'followup' : 'crm';
    setEditingId(null);
    setFormPreset(preset);
    setFormName('');
    setFormEnabled(true);
    setFormPrompt('');
    setFormKnowledge('');
    setFormAutoTransfer(true);
    setFormCalendarEnabled(true);
    setFormHumanTransferEnabled(false);
    setFormHumanTransferRules('');
    setShowForm(true);
    navigate('/ai-agent', { replace: true, state: null });
  }, [location.state, location.key, currentSector?.id, navigate]);

  useEffect(() => {
    const token = SessionService.getToken();
    if (!token) return;
    fetch(`${config.API_BASE_URL}/users/profile`, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    })
      .then(r => r.json())
      .then((json: { data?: { defaultTimezone?: string } }) => {
        const tz = json?.data?.defaultTimezone?.trim();
        if (tz) setProfileTimezone(tz);
      })
      .catch(() => undefined);
  }, []);

  const openCreate = (preset: AgentPreset = 'receptive') => {
    setEditingId(null);
    setFormPreset(preset);
    setFormName('');
    setFormEnabled(true);
    setFormPrompt('');
    setFormKnowledge('');
    setFormAutoTransfer(true);
    setFormCalendarEnabled(true);
    setFormHumanTransferEnabled(false);
    setFormHumanTransferRules('');
    setShowForm(true);
  };

  // Link da Central (?edit=ID): abre a configuração exata do funcionário e limpa o parâmetro.
  const editParam = searchParams.get('edit');
  useEffect(() => {
    const id = Number(editParam);
    if (!id || agents.length === 0) return;
    const agent = agents.find(a => a.id === id);
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
    if (agent) openEdit(agent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editParam, agents]);

  const openEdit = (agent: UnifiedAiAgentDTO) => {
    setEditingId(agent.id);
    if (agent.capReceptive) setFormPreset('receptive');
    else if (agent.capCampaign) setFormPreset('campaign');
    else if (agent.capFollowup) setFormPreset('followup');
    else setFormPreset('crm');
    setFormName(agent.name);
    setFormEnabled(agent.isEnabled);
    setFormPrompt(agent.prompt);
    setFormKnowledge(agent.knowledgeBase);
    setFormAutoTransfer(agent.autoTransferEnabled);
    setFormCalendarEnabled(agent.calendarEnabled ?? true);
    setFormHumanTransferEnabled(agent.humanTransferRulesEnabled);
    setFormHumanTransferRules(agent.humanTransferRules);
    setShowForm(true);
  };

  const handleSetDefaultReceptive = async (agent: UnifiedAiAgentDTO) => {
    if (!currentSector?.id) return;
    const token = SessionService.getToken();
    if (!token) return;
    setIsSaving(true);
    try {
      await saveSectorAiConfig(
        currentSector.id,
        {
          receptiveAiEnabled: receptiveEnabled,
          defaultReceptiveAgentId: agent.id,
          midnightAiReactivationEnabled: midnightReactivation,
        },
        token
      );
      setDefaultReceptiveAgentId(agent.id);
      setDefaultReceptiveAgentName(agent.name);
      addToast(`"${agent.name}" agora é o funcionário padrão do atendimento`, 'success');
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Não foi possível definir o funcionário padrão'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSectorConfig = async () => {
    if (!currentSector?.id) return;
    const token = SessionService.getToken();
    if (!token) return;
    const resolvedDefaultId = pickDefaultReceptiveAgent(agents, defaultReceptiveAgentId);
    if (receptiveEnabled && !resolvedDefaultId) {
      addToast('Crie e ative um funcionário de atendimento antes de ligar a IA nas conversas', 'error');
      return;
    }
    setIsSaving(true);
    try {
      await saveSectorAiConfig(
        currentSector.id,
        {
          receptiveAiEnabled: receptiveEnabled,
          defaultReceptiveAgentId: resolvedDefaultId,
          midnightAiReactivationEnabled: midnightReactivation,
        },
        token
      );
      addToast('Configurações de atendimento salvas', 'success');
      await loadAll(currentSector.id);
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Não foi possível salvar as configurações de atendimento'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAgent = async () => {
    if (!currentSector?.id) return;
    const token = SessionService.getToken();
    if (!token) return;
    const name = formName.trim();
    if (!name) {
      addToast('Informe o nome do funcionário', 'error');
      return;
    }
    const caps = PRESET_CAPS[formPreset];
    setIsSaving(true);
    try {
      if (editingId) {
        await updateUnifiedAgent(
          editingId,
          currentSector.id,
          {
            name,
            isEnabled: formEnabled,
            prompt: formPrompt,
            knowledgeBase: formKnowledge,
            autoTransferEnabled: formAutoTransfer,
            calendarEnabled: formCalendarEnabled,
            humanTransferRulesEnabled: formHumanTransferEnabled,
            humanTransferRules: formHumanTransferRules,
            inactivityTimeoutMinutes: 0,
          },
          token
        );
        addToast('Funcionário atualizado', 'success');
      } else {
        await createUnifiedAgent(
          {
            sectorId: currentSector.id,
            name,
            isEnabled: formEnabled,
            prompt: formPrompt,
            knowledgeBase: formKnowledge,
            autoTransferEnabled: formAutoTransfer,
            calendarEnabled: formCalendarEnabled,
            humanTransferRulesEnabled: formHumanTransferEnabled,
            humanTransferRules: formHumanTransferRules,
            inactivityTimeoutMinutes: 0,
            capReceptive: caps.r,
            capCampaign: caps.c,
            capFollowup: caps.f,
            capCrm: caps.cr,
          },
          token
        );
        addToast('Funcionário criado', 'success');
      }
      setShowForm(false);
      await loadAll(currentSector.id);
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Não foi possível salvar o funcionário'), 'error');
      console.error('[AIAgentPage] save agent failed', e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSuggestAgentFromPdf = (target: 'prompt' | 'knowledge_base') => {
    if (!quoteDoc) return;
    if (target === 'prompt') {
      setFormPrompt(suggestAgentPromptFromDocumentTemplate(quoteDoc.fields, quotePromptCtx));
      addToast('Prompt sugerido alinhado ao template PDF', 'info');
      return;
    }
    setFormKnowledge(suggestAgentKnowledgeFromDocumentTemplate(quoteDoc.fields, quotePromptCtx));
    addToast('Base de conhecimento sugerida para o template PDF', 'info');
  };

  const handleAiOptimize = async (
    type: 'prompt' | 'knowledge_base',
    getText: () => string,
    setText: (v: string) => void
  ) => {
    if (!currentSector?.id) return;
    const text = getText().trim();
    if (!text) {
      addToast('Escreva um rascunho primeiro. A IA completa no mesmo idioma.', 'info');
      return;
    }
    setAiOptimizing(type);
    try {
      const result = await optimizeText(currentSector.id, type, text, editingId ?? undefined, undefined, formPreset);
      setText(result);
      addToast('Texto otimizado', 'success');
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Não foi possível otimizar com IA'), 'error');
    } finally {
      setAiOptimizing(null);
    }
  };

  const editingCapabilities = useMemo(() => {
    if (!editingId) return null;
    const agent = agents.find(a => a.id === editingId);
    if (!agent) return null;
    const caps: string[] = [];
    if (agent.capReceptive) caps.push('Atendimento');
    if (agent.capCampaign) caps.push('Campanha');
    if (agent.capFollowup) caps.push('Follow-up');
    if (agent.capCrm) caps.push('CRM');
    return caps;
  }, [editingId, agents]);

  const handleDeleteAgent = async () => {
    if (!deleteTarget || !currentSector?.id) return;
    const token = SessionService.getToken();
    if (!token) return;
    setIsSaving(true);
    try {
      await deleteUnifiedAgent(deleteTarget.id, currentSector.id, token);
      addToast('Funcionário excluído', 'success');
      setDeleteTarget(null);
      await loadAll(currentSector.id);
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Não foi possível excluir: o funcionário pode estar em uso'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!currentSector) {
    return (
      <div className="ai-agent-wrapper">
        <div className="ai-agent-page">
          <div className="ai-agent-header">
            <div className="ai-agent-header-content">
              <h1>Funcionários</h1>
              <p className="ai-agent-header-description">Crie funcionários aqui. Nas outras telas você só seleciona.</p>
            </div>
          </div>
          <div className="ai-no-sector">
            <h3>Nenhuma conexão selecionada</h3>
            <p>Selecione uma conexão para gerenciar os funcionários</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-agent-wrapper">
      <div className="ai-agent-page">
        <ToastContainer>
          {toasts.map(toast => (
            <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => removeToast(toast.id)} />
          ))}
        </ToastContainer>

        <div className="ai-agent-header">
          <div className="ai-agent-header-content">
            <div className="ai-agent-header__title-row">
              <div className="ai-agent-header__icon"><FiCpu size={22} /></div>
              <div>
                <h1>Funcionários</h1>
                <p className="ai-agent-header-description">
                  Aqui você cria os funcionários. Campanhas, Follow-up, Funil e Conversas só escolhem entre eles.
                </p>
              </div>
            </div>
          </div>
          <div className="ai-agent-header__right">
            <button
              type="button"
              className="ai-sandbox-open-btn"
              onClick={() => setShowSandbox(true)}
              title="Testar um agente em conversa simulada, sem enviar WhatsApp"
            >
              <FiZap size={15} />
              Testar IA (sandbox)
            </button>
            <button
              type="button"
              className="ai-sandbox-open-btn"
              onClick={() => setShowEval(true)}
              title="Rodar eval com percentis, comparação e PDF de cotação"
            >
              <FiBarChart2 size={15} />
              Eval
            </button>
            <div className="ai-connection-badge">
              <FiMessageSquare size={15} />
              {currentSector.name}
            </div>
          </div>
        </div>

        {showSandbox && currentSector.id && (
          <AiSandboxPanel sectorId={currentSector.id} onClose={() => setShowSandbox(false)} />
        )}
        {showEval && currentSector.id && (
          <AgentEvalPanel sectorId={currentSector.id} onClose={() => setShowEval(false)} />
        )}

        <div className="ai-agent-content">
          {showDocumentsTab && (
            <div className="ai-settings-tab-bar ai-hub-main-tabs">
              <button
                type="button"
                className={`ai-settings-tab ${hubTab === 'agents' ? 'ai-settings-tab--active' : ''}`}
                onClick={openAgentsTab}
              >
                Funcionários
              </button>
              <button
                type="button"
                className={`ai-settings-tab ${hubTab === 'documents' ? 'ai-settings-tab--active' : ''}`}
                onClick={openDocumentsTab}
              >
                Documentos
              </button>
            </div>
          )}

          {showDocumentsTab && hubTab === 'documents' && currentSector.id ? (
            <DocumentsTab sectorId={currentSector.id} inboundAiAvailable={inboundAiAvailable} />
          ) : (
          <>
          {isLoading ? (
            <div className="ai-loading">
              <LoadingSpinner size="lg" color="primary" />
              <span>Carregando funcionários...</span>
            </div>
          ) : (
            <div className="ai-hub">
              <div className="ai-hub-stats">
                <div className="ai-hub-stat">
                  <span className="ai-hub-stat__value">{hubStats.total}</span>
                  <span className="ai-hub-stat__label">Funcionários</span>
                </div>
                <div className="ai-hub-stat">
                  <span className="ai-hub-stat__value ai-hub-stat__value--success">{hubStats.active}</span>
                  <span className="ai-hub-stat__label">Ativos</span>
                </div>
                <div className="ai-hub-stat">
                  <span className="ai-hub-stat__value">{hubStats.receptive}</span>
                  <span className="ai-hub-stat__label">No atendimento</span>
                </div>
                <div className="ai-hub-stat">
                  <span className="ai-hub-stat__value">{hubStats.outbound}</span>
                  <span className="ai-hub-stat__label">Em campanha, follow-up e CRM</span>
                </div>
              </div>

              {teamHealth ? (
                <section className="ai-hub-health" aria-label="Saúde e carga dos funcionários">
                  <div className="ai-hub-roster__head">
                    <h3>Saúde e carga</h3>
                    <span>
                      {teamHealth.period.label} · mesmos números da{' '}
                      <button type="button" className="mai-ci-link" onClick={() => navigate('/central-inteligencia?tab=funcionarios')}>
                        Central de Inteligência
                      </button>
                    </span>
                  </div>
                  <CentralAgentCards
                    agents={teamHealth.agents}
                    onOpen={(path) => {
                      const id = Number(new URLSearchParams(path.split('?')[1] || '').get('edit'));
                      const agent = agents.find(a => a.id === id);
                      if (agent) openEdit(agent);
                      else navigate(path);
                    }}
                  />
                </section>
              ) : (
                <>
                  <DigitalTeamRoster agents={agents} onEdit={openEdit} />
                  {teamHealthState === 'loading' && <p className="ai-hub-roster__empty">Carregando saúde e carga dos funcionários…</p>}
                  {teamHealthState === 'error' && <p className="ai-hub-roster__empty">Não foi possível carregar a saúde dos funcionários agora.</p>}
                </>
              )}

              {isMendsVisionEnabled() && currentSector?.id ? (
                <AgentPerformance
                  sectorId={currentSector.id}
                  agentNames={Object.fromEntries(agents.map((a) => [a.id, a.name]))}
                  onOpenAgent={(id) => {
                    const agent = agents.find((a) => a.id === id);
                    if (agent) openEdit(agent);
                  }}
                />
              ) : null}

              <div className="ai-hub-categories">
                <CategorySection
                  capability="receptive"
                  agents={agentsByCategory.receptive}
                  defaultAgentId={defaultReceptiveAgentId}
                  onCreate={openCreate}
                  onEdit={openEdit}
                  onDelete={setDeleteTarget}
                  onSetDefault={handleSetDefaultReceptive}
                >
                  {currentSector.id && (
                    <div className="ai-hub-receptive-settings">
                      <h4 className="ai-hub-receptive-settings__title">Configurações do atendimento</h4>
                      {defaultReceptiveAgentName && (
                        <p className="ai-hub-receptive-settings__default">
                          Funcionário padrão: <strong>{defaultReceptiveAgentName}</strong>
                          <span className="ai-hub-receptive-settings__default-hint"> — use a estrela de um funcionário acima para trocar</span>
                        </p>
                      )}
                      <div className="ai-hub-setting-card">
                        <div className="ai-hub-setting-card__info">
                          <h5>Atendimento por IA ligado</h5>
                          <p>A IA responde quando a conversa está com a IA ativa</p>
                          {showDocumentsTab && (
                            <p className="ai-hub-receptive-settings__doc-hint">
                              As cotações da aba Documentos também valem para os funcionários de campanha e de saída que respondem leads.
                            </p>
                          )}
                        </div>
                        <label className="ai-toggle-switch">
                          <input
                            type="checkbox"
                            checked={receptiveEnabled}
                            onChange={e => {
                              const enabled = e.target.checked;
                              setReceptiveEnabled(enabled);
                              if (enabled) {
                                const autoDefault = pickDefaultReceptiveAgent(agents, defaultReceptiveAgentId);
                                if (autoDefault) {
                                  setDefaultReceptiveAgentId(autoDefault);
                                  const def = agents.find(a => a.id === autoDefault);
                                  setDefaultReceptiveAgentName(def?.name ?? '');
                                }
                              }
                            }}
                          />
                          <span className="ai-toggle-slider" />
                        </label>
                      </div>
                      <div className="ai-hub-setting-card">
                        <div className="ai-hub-setting-card__info">
                          <h5><FiMoon size={14} /> Religar à meia-noite</h5>
                          <p>Religa a IA todo dia entre 00:00 e 00:29 ({timezoneDisplayLabel(profileTimezone)})</p>
                        </div>
                        <label className="ai-toggle-switch">
                          <input type="checkbox" checked={midnightReactivation} onChange={e => setMidnightReactivation(e.target.checked)} />
                          <span className="ai-toggle-slider" />
                        </label>
                      </div>
                      <button type="button" className="ai-save-button ai-hub-settings-save" onClick={() => void handleSaveSectorConfig()} disabled={isSaving}>
                        <FiSave size={15} /> Salvar configurações
                      </button>
                    </div>
                  )}
                </CategorySection>

                <div className="ai-hub-outbound-grid">
                  {(['campaign', 'followup', 'crm'] as const).map(cap => (
                    <CategorySection
                      key={cap}
                      capability={cap}
                      agents={agentsByCategory[cap]}
                      onCreate={openCreate}
                      onEdit={openEdit}
                      onDelete={setDeleteTarget}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          </>
          )}
        </div>

        <SharedModal
          open={showForm}
          onClose={() => setShowForm(false)}
          title={editingId ? 'Editar funcionário' : 'Criar funcionário'}
          size="xl"
          className="ai-hub-agent-modal"
          closeOnOverlayClick={!isSaving}
          footer={
            <>
              <button
                type="button"
                className="shared-modal__btn shared-modal__btn--secondary"
                onClick={() => setShowForm(false)}
                disabled={isSaving}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="shared-modal__btn ai-hub-agent-modal__save"
                onClick={() => void handleSaveAgent()}
                disabled={isSaving}
              >
                {isSaving ? <LoadingSpinner size="sm" color="white" /> : <><FiSave size={16} /> Salvar funcionário</>}
              </button>
            </>
          }
        >
          {editingCapabilities && editingCapabilities.length > 0 && (
            <div className="ai-hub-agent-modal__caps">
              {editingCapabilities.map(cap => (
                <span key={cap} className="ai-hub-agent-modal__cap">{cap}</span>
              ))}
            </div>
          )}

          <div className="ai-hub-agent-modal__section">
            <div className="new-agent-modal__form-group">
              <label className="new-agent-modal__label">Nome do funcionário</label>
              <input
                className="new-agent-modal__input"
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder="Ex.: Assistente de vendas"
                autoFocus
              />
            </div>

            <div className="ai-hub-agent-modal__enabled-card">
              <div className="new-agent-modal__toggle-info">
                <h4>Ativo</h4>
                <p>Funcionários inativos não aparecem para seleção nas outras telas</p>
              </div>
              <label className="new-agent-modal__toggle-switch">
                <input type="checkbox" checked={formEnabled} onChange={e => setFormEnabled(e.target.checked)} />
                <span className="new-agent-modal__toggle-slider" />
              </label>
            </div>
          </div>

          {!editingId && (
            <div className="ai-hub-agent-modal__section">
              <label className="new-agent-modal__label">Onde este funcionário atua</label>
              <p className="ai-hub-agent-modal__section-desc">Escolha um grupo. Atendimento e envios ativos nunca se misturam.</p>
              <div className="ai-hub-type-grid">
                {CREATE_PRESETS.map(preset => {
                  const cap = preset as AiAgentCapability;
                  const meta = CAPABILITY_SECTION_META[cap];
                  const selected = formPreset === preset;
                  return (
                    <button
                      key={preset}
                      type="button"
                      className={`ai-hub-type-card ai-hub-type-card--${preset} ${selected ? 'ai-hub-type-card--selected' : ''}`}
                      onClick={() => setFormPreset(preset)}
                    >
                      <span className={`ai-hub-type-card__icon ai-hub-type-card__icon--${preset}`}>
                        {CATEGORY_ICONS[cap]}
                      </span>
                      <span className="ai-hub-type-card__title">{meta.title}</span>
                      <span className="ai-hub-type-card__desc">{meta.description}</span>
                      {selected && <span className="ai-hub-type-card__check"><FiCheck size={14} /></span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="ai-hub-agent-modal__section">
            <div className="new-agent-modal__form-group">
              <label className="new-agent-modal__label">Instruções do funcionário</label>
              <div className="field-with-optimize">
                <div className="field-with-optimize__header">
                  {showPdfAgentSuggest && (
                    <button
                      type="button"
                      className="field-with-optimize__btn field-with-optimize__btn--secondary"
                      title="Sugere prompt receptivo alinhado ao PDF — sem duplicar campos do template"
                      disabled={aiOptimizing !== null}
                      onClick={() => handleSuggestAgentFromPdf('prompt')}
                    >
                      Sugerir do PDF
                    </button>
                  )}
                  <button
                    type="button"
                    className="field-with-optimize__btn"
                    title={showPdfAgentSuggest ? 'Otimiza com IA e evita conflito com o módulo PDF' : 'Otimizar com IA, mantendo o seu idioma'}
                    disabled={aiOptimizing !== null}
                    onClick={() => void handleAiOptimize('prompt', () => formPrompt, setFormPrompt)}
                  >
                    {aiOptimizing === 'prompt' ? <LoadingSpinner size="sm" color="white" /> : 'Otimizar'}
                  </button>
                </div>
                <textarea
                  className="field-with-optimize__textarea ai-hub-scroll-textarea"
                  value={formPrompt}
                  onChange={e => setFormPrompt(e.target.value)}
                  placeholder={PROMPT_PLACEHOLDERS[formPreset]}
                  rows={6}
                />
              </div>
            </div>
            <div className="new-agent-modal__form-group">
              <label className="new-agent-modal__label">Base de conhecimento</label>
              <div className="field-with-optimize">
                <div className="field-with-optimize__header">
                  {showPdfAgentSuggest && (
                    <button
                      type="button"
                      className="field-with-optimize__btn field-with-optimize__btn--secondary"
                      title="Sugere FAQ geral — campos do PDF ficam no document prompt"
                      disabled={aiOptimizing !== null}
                      onClick={() => handleSuggestAgentFromPdf('knowledge_base')}
                    >
                      Sugerir do PDF
                    </button>
                  )}
                  <button
                    type="button"
                    className="field-with-optimize__btn"
                    title={showPdfAgentSuggest ? 'Otimiza com IA e evita conflito com o módulo PDF' : 'Otimizar com IA, mantendo o seu idioma'}
                    disabled={aiOptimizing !== null}
                    onClick={() => void handleAiOptimize('knowledge_base', () => formKnowledge, setFormKnowledge)}
                  >
                    {aiOptimizing === 'knowledge_base' ? <LoadingSpinner size="sm" color="white" /> : 'Otimizar'}
                  </button>
                </div>
                <textarea
                  className="field-with-optimize__textarea ai-hub-scroll-textarea"
                  value={formKnowledge}
                  onChange={e => setFormKnowledge(e.target.value)}
                  placeholder={KNOWLEDGE_PLACEHOLDER}
                  rows={6}
                />
              </div>
            </div>
          </div>

          {(formPreset === 'receptive' || formPreset === 'campaign') && (
            <div className="ai-hub-agent-modal__section ai-hub-agent-modal__receptive">
              <label className="new-agent-modal__label">{formPreset === 'campaign' ? 'Opções das conversas de campanha' : 'Opções das conversas de atendimento'}</label>
              <div className="ai-hub-agent-modal__enabled-card">
                <div className="new-agent-modal__toggle-info">
                  <h4><FiUsers size={15} /> Permitir transferir para uma pessoa</h4>
                  <p>Com isso ligado, a IA pode passar a conversa para uma pessoa (quando o cliente pede ou quando uma regra de transferência se aplica)</p>
                </div>
                <label className="new-agent-modal__toggle-switch">
                  <input type="checkbox" checked={formAutoTransfer} onChange={e => setFormAutoTransfer(e.target.checked)} />
                  <span className="new-agent-modal__toggle-slider" />
                </label>
              </div>
              <div className="ai-hub-agent-modal__enabled-card">
                <div className="new-agent-modal__toggle-info">
                  <h4><FiCalendar size={15} /> Usar a agenda do setor</h4>
                  <p>Com isso ligado, a IA oferece os horários livres da Agenda e marca o compromisso. Desligue quando o prompt já define o dia e a hora (por exemplo, uma live fixa).</p>
                </div>
                <label className="new-agent-modal__toggle-switch">
                  <input type="checkbox" checked={formCalendarEnabled} onChange={e => setFormCalendarEnabled(e.target.checked)} />
                  <span className="new-agent-modal__toggle-slider" />
                </label>
              </div>
              <div className="ai-hub-agent-modal__enabled-card">
                <div className="new-agent-modal__toggle-info">
                  <h4><FiUser size={15} /> Regras de transferência</h4>
                  <p>A IA transfere no meio da conversa quando uma das suas regras se aplica. Funciona com esta opção ligada ou com &quot;Permitir transferir para uma pessoa&quot; acima.</p>
                </div>
                <label className="new-agent-modal__toggle-switch">
                  <input type="checkbox" checked={formHumanTransferEnabled} onChange={e => setFormHumanTransferEnabled(e.target.checked)} />
                  <span className="new-agent-modal__toggle-slider" />
                </label>
              </div>
              {formHumanTransferEnabled && (
                <div className="new-agent-modal__form-group">
                  <label className="new-agent-modal__label">Quando transferir</label>
                  <textarea
                    className="new-agent-modal__textarea ai-hub-scroll-textarea"
                    value={formHumanTransferRules}
                    onChange={e => setFormHumanTransferRules(e.target.value)}
                    placeholder="Descreva quando a IA deve passar para uma pessoa: contestação de pagamento, cliente irritado, pedido para falar com o gerente..."
                    rows={4}
                  />
                </div>
              )}
            </div>
          )}
        </SharedModal>

        <ConfirmModal
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => void handleDeleteAgent()}
          title="Excluir funcionário"
          message={deleteTarget ? `Excluir "${deleteTarget.name}"? Funcionários em uso não podem ser removidos.` : ''}
          confirmLabel="Delete"
          variant="danger"
          loading={isSaving}
        />
      </div>
    </div>
  );
};

export default AIAgentPage;